import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Save } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsManager = () => {
  const { getAuthHeader } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [contactInfo, setContactInfo] = useState({
    phone: '',
    email: '',
    address: '',
    whatsapp: '',
    hours: ''
  });
  const [stats, setStats] = useState({
    visas_processed: '',
    success_rate: '',
    years_experience: '',
    countries_covered: ''
  });
  const [footerContent, setFooterContent] = useState({
    company_name: 'VISAASURE',
    description: 'Your trusted partner for visa consultation and immigration services. Helping individuals and families achieve their global dreams since 2009.',
    copyright_text: '© 2024 Visaasure. All rights reserved. | Mumbai, India'
  });
  const [quickLinks, setQuickLinks] = useState({
    services: { label: 'Services', enabled: true, url: '#services' },
    top_destinations: { label: 'Top Destinations', enabled: true, url: '#get-visa' },
    process: { label: 'Process', enabled: true, url: '#process' },
    testimonials: { label: 'Testimonials', enabled: true, url: '#testimonials' },
    blogs: { label: 'Blogs', enabled: false, url: '/blogs' },
    contact: { label: 'Contact', enabled: true, url: '#contact' }
  });
  const [selectedLogo, setSelectedLogo] = useState('logo_monogram'); // Default to option 4
  const [helpCenterLinks, setHelpCenterLinks] = useState([
    { label: 'Video Tutorials', url: 'https://youtube.com/@visaasure', enabled: true },
    { label: 'Knowledge Base', url: 'https://docs.visaasure.com', enabled: true },
    { label: 'Contact Support', url: 'mailto:support@visaasure.com', enabled: true }
  ]);
  const [privacyPolicy, setPrivacyPolicy] = useState('');
  const [newsletterConfig, setNewsletterConfig] = useState({
    enabled: true,
    title: '',
    description: '',
    button_text: '',
    placeholder: '',
    success_message: ''
  });
  
  const logoOptions = [
    { 
      value: 'logo_text_based', 
      label: 'Text-Based Logo', 
      description: 'Clean typography with subtle travel elements',
      image: '/logo_text_based.png'
    },
    { 
      value: 'logo_icon_text', 
      label: 'Icon + Text Logo', 
      description: 'Versatile combination for all media',
      image: '/logo_icon_text.png'
    },
    { 
      value: 'logo_icon_only', 
      label: 'Icon-Only Logo', 
      description: 'Minimal symbol for app icons and social media',
      image: '/logo_icon_only.png'
    },
    { 
      value: 'logo_monogram', 
      label: 'Monogram Logo', 
      description: 'Elegant lettermark for premium branding',
      image: '/logo_monogram.png'
    }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      // Fetch contact info
      const contactRes = await axios.get(`${API}/settings/contact_info`);
      setContactInfo(contactRes.data.setting_value);
      
      // Fetch stats
      const statsRes = await axios.get(`${API}/settings/stats`);
      setStats(statsRes.data.setting_value);
      
      // Fetch footer content
      try {
        const footerRes = await axios.get(`${API}/settings/footer_content`);
        setFooterContent(footerRes.data.setting_value);
      } catch (err) {
        console.log('Footer content not set, using defaults');
      }
      
      // Fetch quick links
      try {
        const linksRes = await axios.get(`${API}/settings/quick_links`);
        setQuickLinks(linksRes.data.setting_value);
      } catch (err) {
        console.log('Quick links not set, using defaults');
      }
      
      // Fetch selected logo
      try {
        const logoRes = await axios.get(`${API}/settings/selected_logo`);
        setSelectedLogo(logoRes.data.setting_value);
      } catch (err) {
        console.log('Logo setting not set, using default (monogram)');
      }
      
      // Fetch help center links
      try {
        const helpLinksRes = await axios.get(`${API}/settings/help_center_links`);
        setHelpCenterLinks(helpLinksRes.data.setting_value);
      } catch (err) {
        console.log('Help center links not set, using defaults');
      }
      
      // Fetch privacy policy
      try {
        const privacyRes = await axios.get(`${API}/settings/privacy_policy`);
        setPrivacyPolicy(privacyRes.data.setting_value);
      } catch (err) {
        console.log('Privacy policy not set, using defaults');
      }
      
      // Fetch newsletter config
      try {
        const newsletterRes = await axios.get(`${API}/settings/newsletter_config`);
        setNewsletterConfig(newsletterRes.data.setting_value);
      } catch (err) {
        console.log('Newsletter config not set, using defaults');
      }
      
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveContactInfo = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/admin/settings/contact_info`,
        { setting_value: contactInfo },
        { headers: getAuthHeader() }
      );
      toast({ title: "Success", description: "Contact information updated successfully" });
    } catch (error) {
      console.error('Failed to save:', error);
      toast({
        title: "Error",
        description: "Failed to save contact information",
        variant: "destructive"
      });
    }
  };

  const saveStats = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/admin/settings/stats`,
        { setting_value: stats },
        { headers: getAuthHeader() }
      );
      toast({ title: "Success", description: "Statistics updated successfully" });
    } catch (error) {
      console.error('Failed to save:', error);
      toast({
        title: "Error",
        description: "Failed to save statistics",
        variant: "destructive"
      });
    }
  };

  const saveFooterContent = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/admin/settings/footer_content`,
        { setting_value: footerContent },
        { headers: getAuthHeader() }
      );
      toast({ title: "Success", description: "Footer content updated successfully" });
    } catch (error) {
      console.error('Failed to save:', error);
      toast({
        title: "Error",
        description: "Failed to save footer content",
        variant: "destructive"
      });
    }
  };

  const saveQuickLinks = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/admin/settings/quick_links`,
        { setting_value: quickLinks },
        { headers: getAuthHeader() }
      );
      toast({ title: "Success", description: "Quick links updated successfully" });
    } catch (error) {
      console.error('Failed to save:', error);
      toast({
        title: "Error",
        description: "Failed to save quick links",
        variant: "destructive"
      });
    }
  };

  const saveHelpCenterLinks = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/admin/settings/help_center_links`,
        { setting_value: helpCenterLinks },
        { headers: getAuthHeader() }
      );
      toast({ title: "Success", description: "Help Center links updated successfully" });
    } catch (error) {
      console.error('Failed to save:', error);
      toast({
        title: "Error",
        description: "Failed to save Help Center links",
        variant: "destructive"
      });
    }
  };

  const addHelpCenterLink = () => {
    setHelpCenterLinks([...helpCenterLinks, { label: '', url: '', enabled: true }]);
  };

  const removeHelpCenterLink = (index) => {
    setHelpCenterLinks(helpCenterLinks.filter((_, i) => i !== index));
  };

  const updateHelpCenterLink = (index, field, value) => {
    const updated = [...helpCenterLinks];
    updated[index][field] = value;
    setHelpCenterLinks(updated);
  };


  const savePrivacyPolicy = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/admin/settings/privacy_policy`,
        { setting_value: privacyPolicy },
        { headers: getAuthHeader() }
      );
      toast({ title: "Success", description: "Privacy Policy updated successfully" });
    } catch (error) {
      console.error('Failed to save:', error);
      toast({
        title: "Error",
        description: "Failed to save Privacy Policy",
        variant: "destructive"
      });
    }

  };

  const saveNewsletterConfig = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/admin/settings/newsletter_config`,
        { setting_value: newsletterConfig },
        { headers: getAuthHeader() }
      );
      toast({ title: "Success", description: "Newsletter settings updated successfully" });
    } catch (error) {
      console.error('Failed to save:', error);
      toast({
        title: "Error",
        description: "Failed to save Newsletter settings",
        variant: "destructive"
      });
    }

  };

  
  const saveLogo = async (logoValue) => {
    try {
      await axios.put(
        `${API}/admin/settings/selected_logo`,
        { setting_value: logoValue },
        { headers: getAuthHeader() }
      );
      setSelectedLogo(logoValue);
      toast({ 
        title: "Success", 
        description: "Logo updated successfully. Refresh the page to see changes." 
      });
    } catch (error) {
      console.error('Failed to save:', error);
      toast({
        title: "Error",
        description: "Failed to save logo selection",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="heading-1">Settings</h1>
          <p className="body-large">Manage website content and information</p>
        </div>
      </div>

      <div className="settings-container">
        {/* Logo Selection */}
        <div className="settings-card">
          <h2 className="heading-2">Logo Selection</h2>
          <p className="body-medium" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
            Choose the logo that will appear on your website and admin console
          </p>
          
          <div className="logo-selection-grid">
            {logoOptions.map((logo, index) => (
              <div 
                key={logo.value}
                className={`logo-option-card ${selectedLogo === logo.value ? 'selected' : ''}`}
                onClick={() => saveLogo(logo.value)}
              >
                <div className="logo-option-preview">
                  <img src={logo.image} alt={logo.label} />
                </div>
                <div className="logo-option-info">
                  <div className="logo-option-header">
                    <h3 className="heading-3">{index + 1}. {logo.label}</h3>
                    {selectedLogo === logo.value && (
                      <span className="badge-success">✓ Active</span>
                    )}
                  </div>
                  <p className="body-small">{logo.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Contact Information */}
        <div className="settings-card">
          <h2 className="heading-2">Contact Information</h2>
          <form onSubmit={saveContactInfo} className="settings-form">
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input
                type="text"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                value={contactInfo.email}
                onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Office Address</label>
              <textarea
                value={contactInfo.address}
                onChange={(e) => setContactInfo({...contactInfo, address: e.target.value})}
                className="form-input"
                rows="2"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">WhatsApp Number</label>
              <input
                type="text"
                value={contactInfo.whatsapp}
                onChange={(e) => setContactInfo({...contactInfo, whatsapp: e.target.value})}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Office Hours</label>
              <input
                type="text"
                value={contactInfo.hours}
                onChange={(e) => setContactInfo({...contactInfo, hours: e.target.value})}
                className="form-input"
                placeholder="e.g., Mon-Sat: 10:00 AM - 7:00 PM"
                required
              />
            </div>

            <button type="submit" className="btn-primary">
              <Save size={20} />
              <span>Save Contact Info</span>
            </button>
          </form>
        </div>

        {/* Statistics */}
        <div className="settings-card">
          <h2 className="heading-2">Company Statistics</h2>
          <form onSubmit={saveStats} className="settings-form">
            <div className="form-group">
              <label className="form-label">Visas Processed</label>
              <input
                type="text"
                value={stats.visas_processed}
                onChange={(e) => setStats({...stats, visas_processed: e.target.value})}
                className="form-input"
                placeholder="e.g., 5000+"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Success Rate</label>
              <input
                type="text"
                value={stats.success_rate}
                onChange={(e) => setStats({...stats, success_rate: e.target.value})}
                className="form-input"
                placeholder="e.g., 98%"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Years of Experience</label>
              <input
                type="text"
                value={stats.years_experience}
                onChange={(e) => setStats({...stats, years_experience: e.target.value})}
                className="form-input"
                placeholder="e.g., 15+"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Countries Covered</label>
              <input
                type="text"
                value={stats.countries_covered}
                onChange={(e) => setStats({...stats, countries_covered: e.target.value})}
                className="form-input"
                placeholder="e.g., 50+"
                required
              />
            </div>

            <button type="submit" className="btn-primary">
              <Save size={20} />
              <span>Save Statistics</span>
            </button>
          </form>
        </div>

        {/* Footer Content */}
        <div className="settings-card">
          <h2 className="heading-2">Footer Content</h2>
          <form onSubmit={saveFooterContent} className="settings-form">
            <div className="form-group">
              <label className="form-label">Company Name</label>
              <input
                type="text"
                value={footerContent.company_name}
                onChange={(e) => setFooterContent({...footerContent, company_name: e.target.value})}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Footer Description</label>
              <textarea
                value={footerContent.description}
                onChange={(e) => setFooterContent({...footerContent, description: e.target.value})}
                className="form-input"
                rows="3"
                placeholder="Brief description about your company"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Copyright Text</label>
              <input
                type="text"
                value={footerContent.copyright_text}
                onChange={(e) => setFooterContent({...footerContent, copyright_text: e.target.value})}
                className="form-input"
                placeholder="© 2024 Visaasure. All rights reserved. | Mumbai, India"
                required
              />
            </div>

            <button type="submit" className="btn-primary">
              <Save size={20} />
              <span>Save Footer Content</span>
            </button>
          </form>
        </div>

        {/* Quick Links */}
        <div className="settings-card">
          <h2 className="heading-2">Footer Quick Links</h2>
          <p className="body-small" style={{ marginBottom: '1rem', color: '#64748b' }}>
            Enable/disable navigation links in the footer. Blogs link will be available once Blogs feature is implemented.
          </p>
          <form onSubmit={saveQuickLinks} className="settings-form">
            {Object.entries(quickLinks).map(([key, link]) => (
              <div key={key} className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <input
                  type="checkbox"
                  id={`link-${key}`}
                  checked={link.enabled}
                  onChange={(e) => setQuickLinks({
                    ...quickLinks,
                    [key]: { ...link, enabled: e.target.checked }
                  })}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <label htmlFor={`link-${key}`} style={{ flex: 1, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="body-medium">{link.label}</span>
                  <span className="body-small" style={{ color: '#94a3b8' }}>({link.url})</span>
                </label>
              </div>
            ))}

            <button type="submit" className="btn-primary">
              <Save size={20} />
              <span>Save Quick Links</span>
            </button>
          </form>
        </div>

        {/* Help Center Quick Links */}
        <div className="settings-card">
          <h2 className="heading-2">Help Center Quick Links</h2>
          <p className="body-small" style={{ marginBottom: '1rem', color: '#64748b' }}>
            Configure the quick links displayed at the bottom of the Help Center page. These help users access external resources like video tutorials, documentation, or support.
          </p>
          <form onSubmit={saveHelpCenterLinks} className="settings-form">
            {helpCenterLinks.map((link, index) => (
              <div key={index} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: '1rem', alignItems: 'end' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Label</label>
                    <input
                      type="text"
                      value={link.label}
                      onChange={(e) => updateHelpCenterLink(index, 'label', e.target.value)}
                      className="form-input"
                      placeholder="e.g., Video Tutorials"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">URL</label>
                    <input
                      type="url"
                      value={link.url}
                      onChange={(e) => updateHelpCenterLink(index, 'url', e.target.value)}
                      className="form-input"
                      placeholder="https://example.com or mailto:support@example.com"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeHelpCenterLink(index)}
                    className="btn-secondary"
                    style={{ padding: '0.5rem 1rem', background: '#fee', color: '#c00', border: '1px solid #fcc' }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            
            {helpCenterLinks.length < 5 && (
              <button
                type="button"
                onClick={addHelpCenterLink}
                className="btn-secondary"
                style={{ marginBottom: '1rem' }}
              >
                + Add Link
              </button>
            )}

            <button type="submit" className="btn-primary">
              <Save size={20} />
              <span>Save Help Center Links</span>
            </button>
          </form>
        </div>

        {/* Privacy Policy */}
        <div className="settings-card">
          <h2 className="heading-2">Privacy Policy</h2>
          <p className="body-medium" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
            Manage your privacy policy content. This will be displayed on the /privacy-policy page.
          </p>
          <form onSubmit={savePrivacyPolicy} className="settings-form">
            <div className="form-group">
              <label className="form-label">Privacy Policy Content (Markdown supported)</label>
              <textarea
                value={privacyPolicy}
                onChange={(e) => setPrivacyPolicy(e.target.value)}
                className="form-textarea"
                rows={20}
                placeholder="Enter your privacy policy content here..."
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
              <p className="body-small" style={{ marginTop: '0.5rem', color: 'var(--text-muted)' }}>
                Supports Markdown formatting. Use # for headings, ** for bold, * for italic, etc.
              </p>
            </div>

            <button type="submit" className="btn-primary">
              <Save size={20} />
              <span>Save Privacy Policy</span>
            </button>
          </form>
        </div>

        {/* Newsletter Configuration */}
        <div className="settings-card">
          <h2 className="heading-2">Newsletter Settings</h2>
          <p className="body-medium" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
            Configure the newsletter subscription section displayed in the footer.
          </p>
          <form onSubmit={saveNewsletterConfig} className="settings-form">
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="checkbox"
                  checked={newsletterConfig.enabled}
                  onChange={(e) => setNewsletterConfig({...newsletterConfig, enabled: e.target.checked})}
                  style={{ width: 'auto' }}
                />
                Enable Newsletter Section
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">Title</label>
              <input
                type="text"
                value={newsletterConfig.title}
                onChange={(e) => setNewsletterConfig({...newsletterConfig, title: e.target.value})}
                className="form-input"
                placeholder="e.g., Stay Updated with Visaasure"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={newsletterConfig.description}
                onChange={(e) => setNewsletterConfig({...newsletterConfig, description: e.target.value})}
                className="form-textarea"
                rows={3}
                placeholder="e.g., Subscribe to our newsletter for the latest visa updates..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Button Text</label>
                <input
                  type="text"
                  value={newsletterConfig.button_text}
                  onChange={(e) => setNewsletterConfig({...newsletterConfig, button_text: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Subscribe"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email Placeholder</label>
                <input
                  type="text"
                  value={newsletterConfig.placeholder}
                  onChange={(e) => setNewsletterConfig({...newsletterConfig, placeholder: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Enter your email address"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Success Message</label>
              <input
                type="text"
                value={newsletterConfig.success_message}
                onChange={(e) => setNewsletterConfig({...newsletterConfig, success_message: e.target.value})}
                className="form-input"
                placeholder="e.g., Thank you for subscribing!"
              />
            </div>

            <button type="submit" className="btn-primary">
              <Save size={20} />
              <span>Save Newsletter Settings</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SettingsManager;
